import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { ProgressBar } from "primereact/progressbar";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
// SmilesDrawer loaded via script tag in useEffect for reliable CRA bundling
// Token will be read from sessionStorage (set by KeycloakAuthService)
import "./CompoundVerify.css";

// ====== API 基地址 ======
const API_BASE = (() => {
  try {
    return window._env_?.REACT_APP_WEB_API_BASE_URI || "/api";
  } catch (_) {
    return "/api";
  }
})();

// ====== 获取 token（复用 KeycloakAuthService） ======
const getToken = async () => {
  try {
    const token = sessionStorage.getItem("pkce_access_token");
    if (token) return token;
  } catch (_) {}
  return "";
};

// ====== API 请求封装 ======
const apiGet = async (url) => {
  const token = await getToken();
  const resp = await fetch(`${API_BASE}${url}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!resp.ok) throw new Error(`GET ${url} failed: ${resp.status}`);
  return resp.json();
};

const apiPost = async (url, body) => {
  const token = await getToken();
  const resp = await fetch(`${API_BASE}${url}`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
  if (!resp.ok) throw new Error(`POST ${url} failed: ${resp.status}`);
  return resp.json();
};

// ====== Ketcher 独立页面 URL ======
const KETCHER_URL = "/ketcher/index.html?v=2";

// ====== 状态渲染（确保内容居中） ======
const StatusTag = ({ status, isModified }) => {
  const tags = [];
  if (isModified) {
    tags.push(<Tag key="modified" value="已修改" severity="info" icon="pi pi-pencil" style={{ fontSize: 11, padding: "1px 4px" }} />);
  }
  if (status === "confirmed") {
    tags.push(<Tag key="confirmed" value="已确认" severity="success" icon="pi pi-check-circle" style={{ fontSize: 11, padding: "1px 4px" }} />);
  } else if (!isModified) {
    if (status === "undetermined") {
      tags.push(<Tag key="undetermined" value="待定" severity="warn" icon="pi pi-clock" style={{ fontSize: 11, padding: "1px 4px" }} />);
    } else {
      tags.push(<Tag key="pending" value="待核验" severity="warning" icon="pi pi-clock" style={{ fontSize: 11, padding: "1px 4px" }} />);
    }
  }
  return <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2, width: "100%" }}>{tags}</div>;
};

// ====== 主组件 ======
const CompoundVerify = () => {
  const navigate = useNavigate();
  const toast = useRef(null);

  // 状态
  const [molecules, setMolecules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedMolecule, setSelectedMolecule] = useState(null);
  const [progress, setProgress] = useState({ total: 0, confirmed: 0, modified: 0 });
  const [ketcherReady, setKetcherReady] = useState(false);
  const iframeRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importText, setImportText] = useState("");
  const [filterStatus, setFilterStatus] = useState("pending");
  const [importLoading, setImportLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [pageSize] = useState(20);
  // 分页加载：每次请求两页即 20 条
  const [totalRecords, setTotalRecords] = useState(0);
  const [first, setFirst] = useState(0);
  const PAGE_LOAD = 20;

  // ====== 预加载数据（每次取 PAGE_LOAD 条 = 一页） ======
  const [currentSkip, setCurrentSkip] = useState(0);
  const fetchData = useCallback(async (skip) => {
    const s = skip !== undefined ? skip : currentSkip;
    setLoading(true);
    try {
      const status = filterStatus === "all" ? "" : filterStatus;
      const data = await apiGet(`/compound/verify-list?skip=${s}&take=${PAGE_LOAD}&status=${status}`);
      setMolecules(data.molecules || []);
      setTotalRecords(data.total || 0);
      const raw = data.progress || {};
      setProgress({
        total: raw.total ?? raw.Total ?? 0,
        confirmed: raw.confirmed ?? raw.Confirmed ?? 0,
        modified: raw.modified ?? raw.Modified ?? 0,
        undetermined: raw.undetermined ?? raw.Undetermined ?? 0
      });
      return (data?.molecules) || [];
    } catch (e) {
      console.error("Failed to fetch verify list", e);
      toast.current?.show({ severity: "error", summary: "加载失败", detail: "无法获取核验列表" });
    } finally {
      setLoading(false);
    }
  }, [currentSkip, filterStatus]);

  // currentSkip 或 filterStatus 变化时重新请求数据
  useEffect(() => {
    fetchData(currentSkip);
  }, [currentSkip, filterStatus]);

  // ====== 分页回调 ======
  const onPage = (event) => {
    setFirst(event.first);
    const skip = event.first;
    setCurrentSkip(skip);
    fetchData(skip);
  };

  // ====== 动态加载 SmilesDrawer ======
  const [smilesDrawerReady, setSmilesDrawerReady] = useState(false);
  useEffect(() => {
    if (typeof window.SmilesDrawer !== 'undefined') {
      setSmilesDrawerReady(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/smiles-drawer@1.2.0/dist/smiles-drawer.min.js';
    script.onload = () => setSmilesDrawerReady(true);
    document.head.appendChild(script);
  }, []);

  // ====== 行高控制 ======
  const [rowHeight, setRowHeight] = useState(90);

  // ====== 重绘所有结构图 canvas ======
  const redrawAllCanvases = useCallback(() => {
    if (!smilesDrawerReady) return;
    const SmilesDrawer = window.SmilesDrawer;
    document.querySelectorAll('.smiles-canvas').forEach((el) => {
      const smiles = el.dataset.smiles;
      if (!smiles) return;
      try {
        const parent = el.parentElement;
        const cellWidth = parent?.offsetWidth || 200;
        const cellHeight = parent?.offsetHeight || 60;
        const w = Math.max(cellWidth - 16, 60);
        const h = Math.max(cellHeight - 4, 36);
        // 高 DPI 适配：canvas 实际像素 = 2x 物理像素
        const dpr = window.devicePixelRatio || 1;
        el.width = w * dpr;
        el.height = h * dpr;
        el.style.width = w + "px";
        el.style.height = h + "px";
        const drawer = new SmilesDrawer.Drawer({
          width: w * dpr, height: h * dpr,
          bondThickness: 1.0,
          shortBondLength: 1.0,
          fontSizeScale: 0.8,
          atomVisualization: 'default',
          isomericHighlight: true,
          explicitHydrogens: false,
        });
        SmilesDrawer.parse(
          smiles,
          (tree) => drawer.draw(tree, el.id, "light", false),
          (err) => console.error("SmilesDrawer error:", err)
        );
      } catch (e) {
        console.error("SmilesDrawer render error:", e);
      }
    });
  }, [smilesDrawerReady]);

  // ====== 数据加载后渲染 canvas ======
  useEffect(() => {
    if (smilesDrawerReady && molecules.length > 0) {
      const t1 = setTimeout(() => redrawAllCanvases(), 100);
      const t2 = setTimeout(() => redrawAllCanvases(), 500);
      const t3 = setTimeout(() => redrawAllCanvases(), 1500);
      return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
    }
  }, [smilesDrawerReady, molecules, redrawAllCanvases]);

  // ====== 行高变化时重绘结构图 ======
  useEffect(() => {
    const t = setTimeout(() => redrawAllCanvases(), 50);
    return () => clearTimeout(t);
  }, [rowHeight, redrawAllCanvases]);

  // ====== 筛选切换时重绘结构图 ======
  useEffect(() => {
    if (!smilesDrawerReady || molecules.length === 0) return;
    const t1 = setTimeout(() => redrawAllCanvases(), 150);
    const t2 = setTimeout(() => redrawAllCanvases(), 600);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [filterStatus, smilesDrawerReady, molecules, redrawAllCanvases]);

  // ====== 默认选中第一个分子（首次加载 / 选中的分子已被移除时） ======
  useEffect(() => {
    if (molecules.length > 0) {
      if (!selectedMolecule || !molecules.some((m) => m.id === selectedMolecule.id)) {
        selectMolecule(molecules[0]);
      }
    }
  }, [molecules]);
  
  // ====== Ketcher 操作助手（直接 API 调用，不依赖 postMessage） ======
  // Ketcher 3.12 的 window.ketcher 提供了 setMolecule / getSmiles 等直接 API
  const getKetcherApi = useCallback(() => {
    try {
      return iframeRef.current?.contentWindow?.ketcher || null;
    } catch {
      return null;
    }
  }, []);

  const waitForKetcherApi = useCallback(async (timeout = 8000) => {
    const deadline = Date.now() + timeout;
    while (Date.now() < deadline) {
      const api = getKetcherApi();
      if (api && typeof api.setMolecule === "function") return api;
      await new Promise((r) => setTimeout(r, 300));
    }
    return null;
  }, [getKetcherApi]);

  const sendSmilesToKetcher = useCallback(async (smiles) => {
    setKetcherReady(false);
    const api = await waitForKetcherApi();
    if (!api) {
      console.warn("Ketcher API not available");
      setKetcherReady(true);
      return;
    }
    try {
      await api.setMolecule(smiles || "");
      // 隐藏终端氢/碳标签
      api.editor.setOptions(JSON.stringify({ showHydrogenLabels: "off" }));
      // 默认缩放 55%
      if (api.editor && typeof api.editor.zoom === "function") {
        api.editor.zoom(0.55);
      }
      // 居中显示
      if (api.editor && typeof api.editor.centerStruct === "function") {
        api.editor.centerStruct();
      }
    } catch (e) {
      console.error("setMolecule failed:", e);
    }
    setKetcherReady(true);
  }, [waitForKetcherApi]);

  const getSmilesFromKetcher = useCallback(async () => {
    const api = getKetcherApi();
    if (!api || typeof api.getSmiles !== "function") return "";
    try {
      return await api.getSmiles();
    } catch {
      return "";
    }
  }, [getKetcherApi]);

  const selectMolecule = useCallback(async (molecule) => {
    setSelectedMolecule(molecule);
    const idx = molecules.findIndex((m) => m.id === molecule.id);
    setCurrentIndex(idx);
    const smiles = molecule.modifiedSmiles || molecule.smiles;
    // Wait a tick for iframe to mount, then send smiles
    await new Promise((r) => setTimeout(r, 200));
    await sendSmilesToKetcher(smiles);
  }, [molecules, sendSmilesToKetcher]);

  const handleConfirm = async () => {
    if (!selectedMolecule) return;
    // 如果 SMILES 已被修改（通过 Ketcher），先保存修改再确认
    const currentSmiles = await getSmilesFromKetcher();
    const isModified = currentSmiles && currentSmiles !== selectedMolecule.originalSmiles;
    if (isModified) {
      try {
        await apiPost(`/compound/verify-save/${encodeURIComponent(selectedMolecule.id)}`, {
          smiles: currentSmiles,
          isConfirmed: true,
        });
        toast.current?.show({ severity: "success", summary: "已修改并确认", detail: `${selectedMolecule.id} 的 SMILES 已更新并确认` });
        const fetchDataResult = await fetchData();
        goToNextPending(fetchDataResult);
      } catch (e) {
        toast.current?.show({ severity: "error", summary: "操作失败", detail: String(e) });
      }
    } else {
      try {
        await apiPost(`/compound/verify-confirm/${encodeURIComponent(selectedMolecule.id)}`, {});
        toast.current?.show({ severity: "success", summary: "已确认", detail: `${selectedMolecule.id} 标记为正确` });
        const fetchDataResult = await fetchData();
        goToNextPending(fetchDataResult);
      } catch (e) {
        toast.current?.show({ severity: "error", summary: "操作失败", detail: String(e) });
      }
    }
  };

  const handleSaveModified = async (isConfirmed = false) => {
    if (!selectedMolecule) return;
    const smiles = await getSmilesFromKetcher();
    if (!smiles) {
      toast.current?.show({ severity: "warn", summary: "获取失败", detail: "无法从 Ketcher 获取 SMILES，请重试" });
      return;
    }
    if (smiles === selectedMolecule.originalSmiles && isConfirmed) {
      await handleConfirm();
      return;
    }
    if (smiles === selectedMolecule.originalSmiles) {
      toast.current?.show({ severity: "warn", summary: "无变化", detail: "SMILES 未修改" });
      return;
    }
    try {
      await apiPost(`/compound/verify-save/${encodeURIComponent(selectedMolecule.id)}`, {
        smiles,
        isConfirmed,
      });
      toast.current?.show({
        severity: "success",
        summary: isConfirmed ? "已修改并确认" : "已修改",
        detail: `${selectedMolecule.id} 的 SMILES 已更新`,
      });
      const fetchDataResult = await fetchData();
      goToNextPending(fetchDataResult);
    } catch (e) {
      toast.current?.show({ severity: "error", summary: "保存失败", detail: String(e) });
    }
  };

  const goToNextPending = (data) => {
    const list = data || molecules;
    const nextIdx = list.findIndex(
      (m, i) => i > currentIndex && m.status === "pending"
    );
    if (nextIdx >= 0) {
      selectMolecule(list[nextIdx]);
    } else {
      const firstPending = list.findIndex((m) => m.status === "pending");
      if (firstPending >= 0) {
        selectMolecule(list[firstPending]);
      } else {
        setSelectedMolecule(null);
        setCurrentIndex(-1);
        toast.current?.show({ severity: "success", summary: "全部完成", detail: "所有分子已核验完毕！" });
      }
    }
  };

  const goToPrev = () => {
    if (currentIndex <= 0) return;
    selectMolecule(molecules[currentIndex - 1]);
  };
  const goToNext = () => {
    if (currentIndex >= molecules.length - 1) return;
    selectMolecule(molecules[currentIndex + 1]);
  };

  const handleBatchConfirmCurrentPage = async () => {
    const pendingIds = molecules
      .filter((m) => m.status === "pending")
      .map((m) => m.id);
    if (pendingIds.length === 0) {
      toast.current?.show({ severity: "warn", summary: "无待核验", detail: "当前页面没有待核验的分子" });
      return;
    }
    try {
      const res = await apiPost("/compound/verify-batch-confirm", { ids: pendingIds });
      toast.current?.show({
        severity: "success",
        summary: "批量确认完成",
        detail: `已确认当前页 ${res.confirmed} 个分子`,
      });
      setSelectedIds([]);
      await fetchData();
    } catch (e) {
      toast.current?.show({ severity: "error", summary: "批量确认失败", detail: String(e) });
    }
  };

  const handleImport = async () => {
    if (!importText.trim()) {
      toast.current?.show({ severity: "warn", summary: "无数据", detail: "请输入分子数据" });
      return;
    }
    setImportLoading(true);
    try {
      const lines = importText.trim().split("\n");
      const moleculesData = [];
      for (let i = 0; i < lines.length; i++) {
        const trimmed = lines[i].trim();
        if (!trimmed || trimmed.startsWith("#")) continue;
        // 跳过 CSV 表头行（首行含常见表头关键词）
        if (i === 0 && (trimmed.toLowerCase().includes("smiles") || trimmed.toLowerCase().includes("id") || trimmed.toLowerCase().includes("编号"))) continue;
        const parts = trimmed.includes("\t") ? trimmed.split("\t") : trimmed.split(",");
        if (parts.length >= 2) {
          moleculesData.push({ id: parts[0].trim(), smiles: parts.slice(1).join(",").trim() });
        } else if (parts.length === 1 && parts[0].trim()) {
          moleculesData.push({
            id: `MOL_${String(moleculesData.length + 1).padStart(4, "0")}`,
            smiles: parts[0].trim(),
          });
        }
      }
      if (moleculesData.length === 0) {
        toast.current?.show({ severity: "warn", summary: "无有效数据", detail: "未能解析出分子数据" });
        return;
      }
      const res = await apiPost("/compound/verify-import?clear=true", moleculesData);
      toast.current?.show({ severity: "success", summary: "导入成功", detail: `导入了 ${res.imported} 个分子` });
      setShowImportDialog(false);
      setImportText("");
      await fetchData();
    } catch (e) {
      toast.current?.show({ severity: "error", summary: "导入失败", detail: String(e) });
    } finally {
      setImportLoading(false);
    }
  };

  const toggleFilter = (status) => {
    setFilterStatus(status);
    setCurrentSkip(0);
    setFirst(0);
  };

  const filteredMolecules = filterStatus === "all" ? molecules
    : filterStatus === "modified" ? molecules.filter(m => m.originalSmiles !== m.modifiedSmiles)
    : filterStatus === "confirmed" ? molecules.filter(m => m.status === "confirmed")
    : molecules.filter((m) => m.status === filterStatus);

  const handleExport = async () => {
    try {
      const data = await apiGet("/compound/verify-export");
      if (!data.csv) {
        toast.current?.show({ severity: "warn", summary: "无数据", detail: "没有可导出的数据" });
        return;
      }
      const blob = new Blob([data.csv], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `smiles_verify_${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      toast.current?.show({ severity: "success", summary: "导出成功" });
    } catch (e) {
      toast.current?.show({ severity: "error", summary: "导出失败", detail: String(e) });
    }
  };

  const handleRestoreBackup = async () => {
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/compound-verify/restore-backup`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      toast.current?.show({ severity: "success", summary: "恢复成功", detail: data.message });
      await fetchData();
    } catch (e) {
      toast.current?.show({ severity: "error", summary: "恢复失败", detail: String(e) });
    }
  };

  const selectionCheckbox = (rowData) => {
    if (rowData.status !== "pending") return null;
    return (
      <input
        type="checkbox"
        checked={selectedIds.includes(rowData.id)}
        onChange={(e) => {
          if (e.target.checked) setSelectedIds([...selectedIds, rowData.id]);
          else setSelectedIds(selectedIds.filter((id) => id !== rowData.id));
        }}
        style={{ width: 16, height: 16, cursor: "pointer" }}
      />
    );
  };

  const pendingCount = molecules.filter((m) => m.status === "pending").length;

  // ====== 渲染 ======
  return (
    <div className="flex flex-column w-full fadein animation-duration-500">
      <Toast ref={toast} />

      {/* 标题行 */}
      <div className="flex w-full align-items-center mt-2 mb-2" style={{ padding: "0 4px", minHeight: 28, flexWrap: "nowrap", gap: "6px" }}>
        <i className="pi pi-verified mr-1" style={{ fontSize: 14, color: "#64748b" }} />
        <h1 style={{ margin: 0, fontSize: 20, fontWeight: 600, color: "#334155", whiteSpace: "nowrap" }}>SMILES 结构核验</h1>

        {/* 筛选标签 */}
        <div className="flex align-items-center gap-1 flex-shrink-0" style={{ marginLeft: 12, whiteSpace: "nowrap" }}>
          <span
            className="px-2 py-1"
            style={{
              borderRadius: 4, cursor: "pointer", fontSize: 11,
              background: filterStatus === "all" ? "#e2e8f0" : "#f1f5f9",
              color: filterStatus === "all" ? "#334155" : "#94a3b8",
              border: filterStatus === "all" ? "1px solid #cbd5e1" : "1px solid #e2e8f0",
              fontWeight: filterStatus === "all" ? 600 : 400,
            }}
            onClick={() => toggleFilter("all")}
          >
            全部 ({progress.total || molecules.length})
          </span>
          {["pending", "undetermined", "confirmed", "modified"].map(status => {
            const modifiedCount = progress.modified || 0;
            const confirmedCount = progress.confirmed || 0;
            const undeterminedCount = progress.undetermined || 0;
            const pendingCount = (progress.total || 0) - confirmedCount;
            const count = status === "modified" ? modifiedCount
              : status === "confirmed" ? confirmedCount
              : status === "undetermined" ? undeterminedCount
              : pendingCount;
            const labels = { pending: "待核验", undetermined: "待定", confirmed: "已确认", modified: "已修改" };
            const isActive = filterStatus === status;
            return (
              <span
                key={status}
                className="px-2 py-1"
                style={{
                  borderRadius: 4, cursor: "pointer", fontSize: 11,
                  background: isActive ? (status === "confirmed" ? "#dcfce7" : status === "undetermined" ? "#fef9c3" : status === "modified" ? "#dbeafe" : "#fef3c7") : "#f1f5f9",
                  color: isActive ? "#1e293b" : "#94a3b8",
                  border: isActive ? "1px solid #cbd5e1" : "1px solid #e2e8f0",
                  fontWeight: isActive ? 600 : 400,
                }}
                onClick={() => toggleFilter(status)}
              >
                {labels[status]} ({count})
              </span>
            );
          })}
        </div>

        {progress.total > 0 && (
          <div className="flex align-items-center flex-1" style={{ marginLeft: 12 }}>
            <div className="flex-1">
              <ProgressBar
                color="#667eea"
                value={Math.round((progress.confirmed / progress.total) * 100)}
                displayValueTemplate={() => (
                  <span style={{ fontSize: 11 }}>
                    {progress.confirmed} / {progress.total}
                  </span>
                )}
              />
            </div>
            <div className="flex gap-1" style={{ marginLeft: 6, fontSize: 11, whiteSpace: "nowrap" }}>
              <Tag value={`${progress.confirmed || 0} 已确认`} severity="success" style={{ fontSize: 10, padding: "1px 6px" }} />
              <Tag value={`${progress.modified || 0} 已修改`} severity="info" style={{ fontSize: 10, padding: "1px 6px" }} />
              <Tag value={`${progress.undetermined || 0} 待定`} severity="warn" style={{ fontSize: 10, padding: "1px 6px" }} />
              <Tag value={`${(progress.total || 0) - (progress.confirmed || 0)} 待核验`} severity="warning" style={{ fontSize: 10, padding: "1px 6px" }} />
            </div>
          </div>
        )}
        <div className="flex ml-auto gap-1">
<Button
            label="导出结果"
            icon="pi pi-download"
            className="p-button-sm p-button-outlined"
            style={{ fontSize: 12, padding: "4px 8px" }}
            onClick={handleExport}
          />
          <Button
            label="CSV导入"
            icon="pi pi-upload"
            className="p-button-sm p-button-outlined"
            style={{ fontSize: 12, padding: "4px 8px" }}
            onClick={() => setShowImportDialog(true)}
          />
          <Button
            label="恢复备份"
            icon="pi pi-refresh"
            className="p-button-sm p-button-outlined p-button-warning"
            style={{ fontSize: 12, padding: "4px 8px", display: "none" }}
            onClick={handleRestoreBackup}
          />
        </div>
      </div>

      {/* 主区域：左右分栏 */}
      <div className="flex gap-3" style={{ minHeight: "80vh", alignItems: "stretch" }}>
        {/* 左侧：分子列表 */}
        <div className="flex flex-column" style={{ width: "50%", minHeight: "calc(100vh - 180px)" }}>


                    <DataTable
            key={filterStatus}
            value={filteredMolecules}
            loading={loading}
            first={first}
            onPage={onPage}
            selectionMode="single"
            selection={selectedMolecule}
            onSelectionChange={(e) => e.value && selectMolecule(e.value)}
            paginator
            lazy
            totalRecords={totalRecords}
            rows={pageSize}
            className="w-full datatable-compounds"
            emptyMessage="暂无数据，请导入 CSV"
            scrollable
            scrollHeight="calc(100vh - 250px)"
            sortField="id"
            sortOrder={1}
            size="small"
            dataKey="id"
            showGridlines
          >
            <Column
              header={(() => {
                const pendings = molecules.filter(m => m.status === "pending");
                const allSelected = pendings.length > 0 && pendings.every(m => selectedIds.includes(m.id));
                const someSelected = pendings.some(m => selectedIds.includes(m.id));
                return (
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", width: "100%" }}>
                    <input
                      type="checkbox"
                      ref={(el) => { if (el) el.indeterminate = someSelected && !allSelected; }}
                      checked={allSelected}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedIds(molecules.filter(m => m.status === "pending").map(m => m.id));
                        } else {
                          setSelectedIds([]);
                        }
                      }}
                      style={{ width: 16, height: 16, cursor: "pointer", margin: 0 }}
                    />
                  </div>
                );
              })()}
              body={selectionCheckbox}
            />
            <Column
              field="id"
              header="分子编号"
              sortable
              headerStyle={{ fontSize: 12 }}
              body={(rowData) => (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", lineHeight: 1.4 }}>
                  <span style={{ fontSize: 12, fontWeight: 500, color: "#1e293b" }}>{rowData.id}</span>
                  <StatusTag
                    status={rowData.status}
                    isModified={rowData.originalSmiles !== rowData.modifiedSmiles}
                  />
                </div>
              )}
            />
            <Column
              header="结构"
              headerStyle={{ fontSize: 12 }}
              body={(rowData, { rowIndex }) => {
                const smiles = rowData.modifiedSmiles || rowData.smiles;
                return (
                  <div style={{ position: "relative", width: "100%", minHeight: rowHeight - 12, height: rowHeight - 12 }}>
                    <div className="structure-cell" style={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", height: "100%" }}>
                      <canvas
                        id={`smiles-canvas-${rowData.id}`}
                        className="smiles-canvas"
                        data-smiles={smiles || ""}
                      />
                    </div>
                    <div
                      className="row-resize-handle"
                      data-row-index={rowIndex}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const startY = e.clientY;
                        const startH = rowHeight;
                        const onMove = (ev) => {
                          const newH = Math.max(50, Math.min(300, startH + ev.clientY - startY));
                          setRowHeight(newH);
                        };
                        const onUp = () => {
                          document.removeEventListener('mousemove', onMove);
                          document.removeEventListener('mouseup', onUp);
                        };
                        document.addEventListener('mousemove', onMove);
                        document.addEventListener('mouseup', onUp);
                      }}
                    />
                  </div>
                );
              }}
            />
            <Column
              header="确认"
              headerStyle={{ fontSize: 12 }}
              body={(rowData) => {
                if (rowData.status === "confirmed") {
                  return (
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%" }}>
                      <i className="pi pi-check-circle" style={{ fontSize: 18, color: "#22c55e" }} />
                    </div>
                  );
                }
                return (
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "3px", height: "100%", padding: "2px 0" }}>
                    <Button
                      label="待定"
                      icon="pi pi-clock"
                      className="p-button-sm p-button-warning"
                      style={{ fontSize: 11, padding: "2px 6px", minWidth: 0, whiteSpace: "nowrap", width: "76px" }}
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          await apiPost(`/compound/verify-undetermined/${encodeURIComponent(rowData.id)}`, {});
                          toast.current?.show({ severity: "warn", summary: "已标为待定", detail: `${rowData.id} 标记为待定` });
                          await fetchData();
                        } catch (err) {
                          toast.current?.show({ severity: "error", summary: "操作失败", detail: String(err) });
                        }
                      }}
                    />

                    <div style={{ height: 4 }} />

                    <Button
                      label="确认正确"
                      icon="pi pi-check"
                      className="p-button-sm p-button-success"
                      style={{ fontSize: 11, padding: "2px 6px", minWidth: 0, whiteSpace: "nowrap", width: "76px" }}
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          // 如果 SMILES 被修改过，先保存修改再确认
                          if (rowData.originalSmiles !== rowData.modifiedSmiles) {
                            await apiPost(`/compound/verify-save/${encodeURIComponent(rowData.id)}`, {
                              smiles: rowData.modifiedSmiles,
                              isConfirmed: true,
                            });
                          } else {
                            await apiPost(`/compound/verify-confirm/${encodeURIComponent(rowData.id)}`, {});
                          }
                          toast.current?.show({ severity: "success", summary: "已确认", detail: `${rowData.id} 标记为正确` });
                          await fetchData();
                        } catch (err) {
                          toast.current?.show({ severity: "error", summary: "确认失败", detail: String(err) });
                        }
                      }}
                    />
                  </div>
                );
              }}
            />
          </DataTable>

          {/* 底部：选中确认按钮 */}
          {selectedIds.length > 0 && (
            <div className="flex justify-content-center align-items-center" style={{ padding: "12px 0 4px 0" }}>
              <Button
                label={`确认正确 (${selectedIds.length})`}
                icon="pi pi-check-circle"
                className="p-button-rounded p-button-success"
                style={{ fontSize: 14, padding: "10px 28px", fontWeight: 600, letterSpacing: 1 }}
                onClick={async () => {
                  try {
                    const res = await apiPost("/compound/verify-batch-confirm", { ids: selectedIds });
                    toast.current?.show({
                      severity: "success",
                      summary: "批量确认完成",
                      detail: `已确认 ${res.confirmed} 个分子`,
                    });
                    setSelectedIds([]);
                    await fetchData();
                  } catch (e) {
                    toast.current?.show({ severity: "error", summary: "确认失败", detail: String(e) });
                  }
                }}
              />
            </div>
          )}
        </div>

        {/* 右侧：核验面板 */}
        <div className="flex flex-column" style={{ width: "50%", minHeight: "calc(100vh - 180px)" }}>
          {!selectedMolecule ? (
            <div
              className="flex align-items-center justify-content-center"
              style={{
                height: "calc(100% - 40px)", minHeight: "calc(100vh - 160px)",
                background: "#f8fafc", borderRadius: "12px",
                border: "2px dashed #e2e8f0", color: "#94a3b8", fontSize: 16,
              }}
            >
              <div className="flex flex-column align-items-center gap-2">
                <i className="pi pi-arrow-left" style={{ fontSize: 32 }} />
                <span>请从左侧选择一个分子进行核验</span>
              </div>
            </div>
          ) : (
            <div className="flex flex-column" style={{ flex: 1 }}>
              <div
                className="flex align-items-center justify-content-between p-2"
                style={{
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  borderRadius: 10, color: "#fff",
                }}
              >
                <div>
                  <div className="text-lg font-bold">{selectedMolecule.id}</div>
                  <div className="text-sm" style={{ opacity: 0.85 }}>
                    SMILES: {selectedMolecule.originalSmiles?.substring(0, 80)}
                    {selectedMolecule.originalSmiles?.length > 80 ? "..." : ""}
                  </div>
                  {selectedMolecule.originalSmiles !== selectedMolecule.modifiedSmiles && (
                    <div className="text-sm" style={{ opacity: 0.85, color: "#ffd700" }}>
                      已修改: {selectedMolecule.modifiedSmiles?.substring(0, 80)}
                    </div>
                  )}
                </div>
                <StatusTag
                  status={selectedMolecule.status}
                  isModified={selectedMolecule.originalSmiles !== selectedMolecule.modifiedSmiles}
                />
              </div>

              <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, overflow: "hidden", background: "#fff", display: "flex", flexDirection: "column", flex: 1 }}>
                <div
                  className="flex align-items-center px-3 py-2"
                  style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0", fontSize: 12, color: "#64748b" }}
                >
                  <i className="pi pi-pencil mr-2" />
                  Ketcher 编辑器 — 拖拽修改分子结构
                  {!ketcherReady && (
                    <span className="ml-2" style={{ color: "#f59e0b" }}>(加载中...)</span>
                  )}
                </div>
                <iframe
                  ref={iframeRef}
                  src={KETCHER_URL}
                  title="Ketcher"
                  style={{ width: "100%", flex: 1, border: "none", minHeight: 200 }}
                  onLoad={() => {
                    if (selectedMolecule) {
                      const smiles = selectedMolecule.modifiedSmiles || selectedMolecule.smiles;
                      // Wait for Ketcher API to be ready, then send molecule
                      setTimeout(() => sendSmilesToKetcher(smiles), 500);
                    }
                  }}
                />
              </div>

              <div className="flex align-items-center justify-content-between mt-1">
                <div className="flex gap-2">
                  <Button
                    icon="pi pi-chevron-left"
                    label="上一个"
                    className="p-button-sm p-button-outlined p-button-secondary"
                    onClick={goToPrev}
                    disabled={currentIndex <= 0}
                  />
                  <span className="flex align-items-center px-2" style={{ fontSize: 12, color: "#64748b" }}>
                    {currentIndex + 1} / {molecules.length}
                  </span>
                  <Button
                    icon="pi pi-chevron-right"
                    iconPos="right"
                    label="下一个"
                    className="p-button-sm p-button-outlined p-button-secondary"
                    onClick={goToNext}
                    disabled={currentIndex >= molecules.length - 1}
                  />
                </div>

                <div className="flex gap-2">
                  <Button
                    icon="pi pi-check"
                    label="确认正确"
                    className="p-button-sm p-button-success"
                    onClick={handleConfirm}
                    disabled={selectedMolecule.status === "confirmed"}
                  />
                  <Button
                    icon="pi pi-save"
                    label="修改并确认"
                    className="p-button-sm p-button-info"
                    onClick={() => handleSaveModified(true)}
                  />
                  <Button
                    icon="pi pi-save"
                    label="仅保存修改"
                    className="p-button-sm p-button-warning"
                    onClick={() => handleSaveModified(false)}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 导入对话框 */}
      <Dialog
        header="导入 SMILES 数据"
        visible={showImportDialog}
        onHide={() => setShowImportDialog(false)}
        style={{ width: 600 }}
        footer={
          <div className="flex justify-content-end gap-2">
            <Button
              label="取消" icon="pi pi-times"
              className="p-button-sm p-button-outlined"
              onClick={() => setShowImportDialog(false)}
            />
            <Button
              label="导入" icon="pi pi-upload"
              className="p-button-sm p-button-success"
              onClick={handleImport}
              loading={importLoading}
            />
          </div>
        }
      >
        <div className="flex flex-column gap-2">
          <input
            type="file"
            accept=".csv"
            onChange={(e) => {
              const file = e.target.files[0];
              if (!file) return;
              const reader = new FileReader();
              reader.onload = (ev) => setImportText(ev.target.result);
              reader.readAsText(file);
            }}
            style={{ marginBottom: 8, width: "100%" }}
          />
          <label htmlFor="importCsv" className="font-medium">
            每行一个分子，格式：<code>分子编号,SMILES</code>
          </label>
          <InputTextarea
            id="importCsv"
            value={importText}
            onChange={(e) => setImportText(e.target.value)}
            placeholder={`I-001,CC(=O)Oc1ccccc1C(=O)O\nI-002,CCO\nI-003,c1ccccc1`}
            style={{ width: "100%", minHeight: 200, fontFamily: "monospace", fontSize: 13 }}
            autoResize
          />
          <div className="text-xs" style={{ color: "#94a3b8" }}>
            也支持制表符分隔，或每行只有 SMILES（自动生成编号 MOL_0001）
          </div>
        </div>
      </Dialog>

      {/* 样式 */}
    </div>
  );
};

export default CompoundVerify;